import { cx } from '@vizit/theme';
import * as React from 'react';
import {
  ToggleButton as AriaToggleButton,
  type ToggleButtonProps as AriaToggleButtonProps,
  composeRenderProps,
} from 'react-aria-components';
import { buttonClasses } from '../button/styles';
import { ButtonLevel, ButtonSize } from '../button/types';

interface ToggleButtonProps extends AriaToggleButtonProps {
  /**
   * The size of the toggle button, matching the Button size scale
   *
   * @optional
   * @default 'sm'
   * @type 'xl' | 'lg' | 'md' | 'sm' | 'xs'
   */
  size?: ButtonSize;
  /**
   * The left icon of the toggle button
   *
   * @optional
   * @type React.ReactNode
   */
  iconStart?: React.ReactNode;
  /**
   * The right icon of the toggle button
   *
   * @optional
   * @type React.ReactNode
   */
  iconEnd?: React.ReactNode;
}

/**
 * A two-state button that renders with the design system's Button visuals:
 * secondary when off, primary when on. Use it for filter toggles and other
 * pressed/unpressed controls; selection state and `aria-pressed` come from
 * React Aria.
 *
 * @param props - The props for the ToggleButton component.
 * @example
 * <ToggleButton aria-label="Show uploads only" isSelected={uploadsOnly} onChange={setUploadsOnly} iconStart={<ImageSparkIcon size={20} aria-hidden />} />
 *
 * Toggle buttons that are only icons should have an aria-label prop.
 */
function ToggleButton(props: ToggleButtonProps, ref: React.ForwardedRef<HTMLButtonElement>) {
  const {
    children,
    className: classNameProp,
    isDisabled = false,
    size = ButtonSize.Sm,
    iconStart,
    iconEnd,
    ...other
  } = props;

  const isIconOnly = !!(iconStart || iconEnd) && !children;

  return (
    <AriaToggleButton
      {...other}
      ref={ref}
      isDisabled={isDisabled}
      className={composeRenderProps(classNameProp, (className, renderProps) =>
        cx(
          'toggle-button group',
          buttonClasses({
            ...renderProps,
            level: renderProps.isSelected ? ButtonLevel.Primary : ButtonLevel.Secondary,
            size,
            isDisabled,
            isLoading: false,
            isIconOnly,
          }),
          // Radius and border both ship on the Secondary level only, so
          // flipping to Primary on selection would square the corners and drop
          // a 1px border the auto width was counting -- the button changed
          // shape and jumped as you clicked it. Pin both so the box is
          // identical in either state. Disabled selected already carries its
          // own border, so this only fills the enabled gap.
          'rounded-xs',
          renderProps.isSelected && !isDisabled && 'border border-transparent',
          className,
        ),
      )}
    >
      {(value) => (
        <>
          {iconStart}
          {typeof children === 'function' ? children(value) : children}
          {iconEnd}
        </>
      )}
    </AriaToggleButton>
  );
}

const _ToggleButton = React.forwardRef<HTMLButtonElement, ToggleButtonProps>(ToggleButton);

export { _ToggleButton as ToggleButton };
export type { ToggleButtonProps };
